import React from "react";

import {
  listConversationMessages,
  TCommunicationsMessage,
} from "../../../../../services/api-communications";
import { TSession } from "../../../../../utils/types";
import { toUserFacingErrorMessage } from "../utils/errors";

type UseConversationMessagesParams = {
  session: TSession | null | undefined;
  conversationUuid: string | null;
  enabled?: boolean;
  pollIntervalMs?: number;
};

type TRefreshOptions = {
  silent?: boolean;
};

type TOptimisticMessageParams = {
  conversationUuid: string;
  clientRequestId: string;
  fromE164: string;
  toE164: string;
  body: string;
};

const toMessageTimestamp = (message: TCommunicationsMessage): number => {
  const rawValue =
    message.provider_created_at || message.created_at || message.updated_at;
  const parsed = new Date(rawValue).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const mergeMessages = (
  remoteMessages: TCommunicationsMessage[],
  optimisticMessages: TCommunicationsMessage[],
): TCommunicationsMessage[] => {
  const remoteByClientRequestId = new Set(
    remoteMessages
      .map((message) => (message.client_request_id || "").trim())
      .filter((value) => !!value),
  );

  const unresolvedOptimisticMessages = optimisticMessages.filter((message) => {
    const clientRequestId = (message.client_request_id || "").trim();
    if (!clientRequestId) {
      return true;
    }
    return !remoteByClientRequestId.has(clientRequestId);
  });

  return [...remoteMessages, ...unresolvedOptimisticMessages].sort(
    (left, right) => toMessageTimestamp(left) - toMessageTimestamp(right),
  );
};

export const useConversationMessages = ({
  session,
  conversationUuid,
  enabled = true,
  pollIntervalMs = 4000,
}: UseConversationMessagesParams) => {
  const [messages, setMessages] = React.useState<TCommunicationsMessage[]>([]);
  const [optimisticMessages, setOptimisticMessages] = React.useState<
    TCommunicationsMessage[]
  >([]);
  const [total, setTotal] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  const refresh = React.useCallback(
    async ({ silent = false }: TRefreshOptions = {}) => {
      if (!session || !enabled || !conversationUuid) {
        return;
      }

      if (!silent) {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await listConversationMessages(
          session,
          conversationUuid,
          {
            limit: 200,
            offset: 0,
          },
        );
        const remoteMessages = (response.results || []).sort(
          (left, right) => toMessageTimestamp(left) - toMessageTimestamp(right),
        );

        setOptimisticMessages((currentOptimisticMessages) => {
          const merged = mergeMessages(
            remoteMessages,
            currentOptimisticMessages,
          );
          setMessages(merged);
          return currentOptimisticMessages.filter((message) => {
            const clientRequestId = (message.client_request_id || "").trim();
            if (!clientRequestId) {
              return true;
            }
            return !remoteMessages.some(
              (remoteMessage) =>
                (remoteMessage.client_request_id || "").trim() ===
                clientRequestId,
            );
          });
        });

        setTotal(
          typeof response.total === "number"
            ? response.total
            : remoteMessages.length,
        );
      } catch (nextError) {
        setError(
          toUserFacingErrorMessage(
            nextError,
            "Unable to load this conversation.",
          ),
        );
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [conversationUuid, enabled, session],
  );

  const appendOptimisticOutbound = React.useCallback(
    ({
      conversationUuid: optimisticConversationUuid,
      clientRequestId,
      fromE164,
      toE164,
      body,
    }: TOptimisticMessageParams) => {
      const nowIso = new Date().toISOString();
      const optimisticMessage: TCommunicationsMessage = {
        message_uuid: `optimistic-${clientRequestId}`,
        conversation_uuid: optimisticConversationUuid,
        provider_message_sid: null,
        client_request_id: clientRequestId,
        direction: "outbound",
        status: "queued",
        status_reason: null,
        from_e164: fromE164,
        to_e164: toE164,
        body,
        segment_count: null,
        price: null,
        price_unit: null,
        is_billable: false,
        billing_unit_count: 0,
        provider_created_at: nowIso,
        provider_updated_at: nowIso,
        last_status_event_at: nowIso,
        created_at: nowIso,
        updated_at: nowIso,
      };

      setOptimisticMessages((currentMessages) => {
        const withoutDuplicate = currentMessages.filter(
          (message) =>
            (message.client_request_id || "").trim() !== clientRequestId,
        );
        const nextOptimisticMessages = [...withoutDuplicate, optimisticMessage];
        setMessages((currentRenderedMessages) =>
          mergeMessages(currentRenderedMessages, [optimisticMessage]),
        );
        return nextOptimisticMessages;
      });
    },
    [],
  );

  const reconcileOptimisticOutbound = React.useCallback(
    (params: {
      clientRequestId: string;
      messageUuid: string;
      status: string;
    }) => {
      const { clientRequestId, messageUuid, status } = params;

      setOptimisticMessages((currentMessages) =>
        currentMessages.map((message) => {
          if ((message.client_request_id || "").trim() !== clientRequestId) {
            return message;
          }
          return {
            ...message,
            message_uuid: messageUuid,
            status,
            updated_at: new Date().toISOString(),
          };
        }),
      );

      setMessages((currentMessages) =>
        currentMessages.map((message) => {
          if ((message.client_request_id || "").trim() !== clientRequestId) {
            return message;
          }
          return {
            ...message,
            message_uuid: messageUuid,
            status,
            updated_at: new Date().toISOString(),
          };
        }),
      );
    },
    [],
  );

  const markOptimisticOutboundFailed = React.useCallback(
    (params: { clientRequestId: string; reason: string }) => {
      const { clientRequestId, reason } = params;

      const markAsFailed = (message: TCommunicationsMessage) => {
        if ((message.client_request_id || "").trim() !== clientRequestId) {
          return message;
        }

        return {
          ...message,
          status: "failed",
          status_reason: reason,
          updated_at: new Date().toISOString(),
        };
      };

      setOptimisticMessages((currentMessages) =>
        currentMessages.map(markAsFailed),
      );
      setMessages((currentMessages) => currentMessages.map(markAsFailed));
    },
    [],
  );

  React.useEffect(() => {
    if (!enabled || !conversationUuid) {
      return;
    }

    refresh().catch(() => undefined);
  }, [conversationUuid, enabled, refresh]);

  React.useEffect(() => {
    if (!enabled || !session || !conversationUuid) {
      return;
    }

    const intervalId = setInterval(
      () => {
        refresh({ silent: true }).catch(() => undefined);
      },
      Math.max(pollIntervalMs, 2000),
    );

    return () => {
      clearInterval(intervalId);
    };
  }, [conversationUuid, enabled, pollIntervalMs, refresh, session]);

  React.useEffect(() => {
    if (conversationUuid) {
      return;
    }

    setMessages([]);
    setOptimisticMessages([]);
    setTotal(0);
    setLoading(false);
    setError(null);
  }, [conversationUuid]);

  React.useEffect(() => {
    if (session) {
      return;
    }

    setMessages([]);
    setOptimisticMessages([]);
    setTotal(0);
    setLoading(false);
    setError(null);
  }, [session]);

  return {
    messages,
    total,
    loading,
    error,
    refresh,
    appendOptimisticOutbound,
    reconcileOptimisticOutbound,
    markOptimisticOutboundFailed,
  };
};

export default useConversationMessages;
